import React, { useState } from "react";
import { Box, IconButton, Stack, Typography, Fade, Card } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";

interface PhotoCarouselProps {
  photos: string[];
  itemName: string;
}

const PhotoCarousel: React.FC<PhotoCarouselProps> = ({ photos, itemName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasPhotos = photos && photos.length > 0;
  const hasMultiplePhotos = photos && photos.length > 1;

  // Reset currentIndex if it's out of bounds (e.g., if photos array changes)
  React.useEffect(() => {
    if (hasPhotos && currentIndex >= photos.length) {
      setCurrentIndex(0);
    }
  }, [photos, currentIndex, hasPhotos]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
  };

  if (!hasPhotos) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 3,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: "8rem",
            opacity: 0.3,
            background: "linear-gradient(135deg, #007AFF 0%, #5AC8FA 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          📦
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ opacity: 0.6, fontWeight: 500 }}
        >
          No Image Available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Main Image */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Fade in={true} key={currentIndex} timeout={300}>
          <img
            src={photos[currentIndex]}
            alt={`${itemName} - ${currentIndex + 1}`}
            onError={(e) => {
              console.warn(
                `Failed to load carousel image ${currentIndex + 1} for ${itemName}:`,
                photos[currentIndex]
              );
              e.currentTarget.style.display = "none";
            }}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Fade>

        {/* Navigation Arrows */}
        {hasMultiplePhotos && (
          <>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: "absolute",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "background.paper",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                width: 48,
                height: 48,
                "&:hover": {
                  backgroundColor: "white",
                  transform: "translateY(-50%) scale(1.1)",
                },
                transition: "all 0.2s ease",
                zIndex: 5,
              }}
            >
              <ArrowBack />
            </IconButton>

            <IconButton
              onClick={handleNext}
              sx={{
                position: "absolute",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "background.paper",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                width: 48,
                height: 48,
                "&:hover": {
                  backgroundColor: "white",
                  transform: "translateY(-50%) scale(1.1)",
                },
                transition: "all 0.2s ease",
                zIndex: 5,
              }}
            >
              <ArrowForward />
            </IconButton>
          </>
        )}

        {/* Photo Counter */}
        {hasMultiplePhotos && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(10px)",
              borderRadius: 2,
              px: 2,
              py: 1,
              zIndex: 5,
            }}
          >
            <Typography variant="body2" color="white" fontWeight={600}>
              {currentIndex + 1} / {photos.length}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Thumbnail Strip */}
      {hasMultiplePhotos && (
        <Box
          sx={{
            height: 80,
            p: 2,
            backgroundColor: "rgba(248, 249, 250, 0.9)",
            backdropFilter: "blur(10px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <Stack direction="row" spacing={1} sx={{ height: "100%" }}>
            {photos.map((photo, index) => (
              <Card
                key={index}
                onClick={() => handleThumbnailClick(index)}
                sx={{
                  width: 64,
                  height: 64,
                  cursor: "pointer",
                  border: index === currentIndex ? 2 : 1,
                  borderColor:
                    index === currentIndex ? "primary.main" : "divider",
                  borderStyle: "solid",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                    borderColor: "primary.main",
                  },
                }}
              >
                <img
                  src={photo}
                  alt={`${itemName} thumbnail ${index + 1}`}
                  onError={(e) => {
                    console.warn(
                      `Failed to load thumbnail ${index + 1} for ${itemName}:`,
                      photo
                    );
                    e.currentTarget.style.display = "none";
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Card>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PhotoCarousel;
